import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const resp = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.error || 'Failed to process request');
      }

      setSubmitted(true);
      setResetToken(data.resetToken ?? null);
    } catch (err: any) {
      setError(err.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black uppercase tracking-widest text-navy-900 dark:text-cream-50 mb-2">
            Reset Password
          </h1>
          <p className="text-navy-400 dark:text-navy-300 text-sm">
            Enter your email to receive a reset token
          </p>
        </div>

        <div className="glass-panel rounded-lg shadow-xl p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-600 dark:text-red-400 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          {submitted ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-navy-900 dark:text-cream-50 font-bold mb-2">
                Request received
              </p>
              <p className="text-navy-500 dark:text-cream-300 text-sm mb-4">
                If an account with that email exists, a reset token has been generated.
              </p>
              {resetToken && (
                <div className="bg-cream-100 dark:bg-navy-900/50 border border-gold-400 rounded-lg p-4 mb-4 text-left">
                  <p className="text-xs font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-2">
                    Reset Token (Testing)
                  </p>
                  <p className="mono text-xs text-navy-700 dark:text-cream-200 break-all">
                    {resetToken}
                  </p>
                  <p className="text-xs text-navy-400 dark:text-navy-300 mt-2">
                    Use this token at the reset password page.
                  </p>
                </div>
              )}
              {resetToken && (
                <Link
                  to={`/reset-password?token=${encodeURIComponent(resetToken)}`}
                  className="inline-block bg-navy-700 hover:bg-navy-800 active:bg-navy-900 text-cream-50 px-6 py-3 rounded-lg font-black uppercase tracking-widest text-sm transition"
                >
                  Continue to Reset
                </Link>
              )}
              {!resetToken && (
                <Link
                  to="/login"
                  className="inline-block bg-navy-700 hover:bg-navy-800 active:bg-navy-900 text-cream-50 px-6 py-3 rounded-lg font-black uppercase tracking-widest text-sm transition"
                >
                  Back to Login
                </Link>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-black uppercase tracking-widest mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-cream-50 dark:bg-navy-900 text-navy-900 dark:text-cream-50 border border-cream-300 dark:border-navy-600 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold-500 hover:bg-gold-600 active:bg-gold-700 text-navy-900 font-black uppercase tracking-widest text-sm py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Request Reset Token'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 text-sm font-bold uppercase tracking-widest transition"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
